import React, { useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { Copy, Eraser, RotateCcw, AlignLeft } from 'lucide-react';
import * as prettier from 'prettier';
import parserBabel from 'prettier/parser-babel';

interface CodeEditorWithMonacoProps {
  value: string;
  onChange: (value: string) => void;
  language?: 'python' | 'cpp' | 'javascript';
  height?: string;
  placeholder?: string;
  label?: string;
}

const CodeEditorWithMonaco: React.FC<CodeEditorWithMonacoProps> = ({
  value,
  onChange,
  language = 'python',
  height = '300px',
  placeholder = '',
  label
}) => {
  // 复制代码
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(value);
  }, [value]);

  // 清空代码
  const handleClear = useCallback(() => {
    onChange('');
  }, [onChange]);

  // 恢复默认模板
  const handleReset = useCallback(() => {
    const defaults: Record<string, string> = {
      python: '# 请在此处编写Python代码\n\ndef solution():\n    # TODO: 实现你的逻辑\n    pass\n\nif __name__ == "__main__":\n    solution()',
      cpp: '// 请在此处编写C++代码\n#include <iostream>\nusing namespace std;\n\nint main() {\n    // TODO: 实现你的逻辑\n    return 0;\n}',
      javascript: '// 请在此处编写JavaScript代码\nfunction solution() {\n    // TODO: 实现你的逻辑\n}\n\nsolution();'
    };
    onChange(defaults[language] || '');
  }, [onChange, language]);

  // 格式化代码
  const handleFormat = useCallback(async () => {
    try {
      let formatted = value;
      if (language === 'python') {
        // Python简单格式化：统一缩进为4空格
        formatted = value
          .split('\n')
          .map(line => {
            const indent = line.match(/^(\s*)/)?.[1] || '';
            const spaces = Math.floor(indent.length / 4) * 4;
            return ' '.repeat(spaces) + line.trim();
          })
          .join('\n');
      } else {
        // JS/CPP使用prettier
        formatted = await prettier.format(value, {
          parser: language === 'cpp' ? 'babel' : 'babel',
          plugins: [parserBabel],
          tabWidth: 4,
          useTabs: false,
        });
      }
      onChange(formatted);
    } catch (error) {
      console.warn('代码格式化失败:', error);
      // 格式化失败时静默处理，不影响用户编辑
    }
  }, [value, onChange, language]);

  return (
    <div className="space-y-2">
      {label && (
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">{label}</label>
          <div className="flex items-center gap-1">
            <button
              onClick={handleFormat}
              className="p-1.5 text-gray-500 hover:text-blue-500 hover:bg-blue-50 rounded"
              title="格式化代码"
            >
              <AlignLeft className="w-4 h-4" />
            </button>
            <button
              onClick={handleCopy}
              className="p-1.5 text-gray-500 hover:text-blue-500 hover:bg-blue-50 rounded"
              title="复制代码"
            >
              <Copy className="w-4 h-4" />
            </button>
            <button
              onClick={handleReset}
              className="p-1.5 text-gray-500 hover:text-orange-500 hover:bg-orange-50 rounded"
              title="恢复默认模板"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              onClick={handleClear}
              className="p-1.5 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded"
              title="清空代码"
            >
              <Eraser className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <Editor
          height={height}
          defaultLanguage={language}
          value={value}
          onChange={(val) => onChange(val || '')}
          options={{
            minimap: { enabled: false },
            fontSize: 14,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            automaticLayout: true,
            wordWrap: 'on',
            tabSize: 4,
            insertSpaces: true,
            renderWhitespace: 'selection',
            bracketPairColorization: { enabled: true },
            guides: { bracketPairs: true },
          }}
          theme="vs"
        />
      </div>
    </div>
  );
};

export default CodeEditorWithMonaco;
